<template>
  <div
    class="restriktionen-date-picker"
    :style="restriktionenFarbVariablen"
    @mouseover="onKalenderMouseOver"
    @mouseleave="clearHover"
  >
    <AppDatePicker
      v-bind="$attrs"
      :value="value"
      :disabled-date="istDatumDeaktiviert"
      :get-classes="getZellenKlassen"
      @input="$emit('input', $event)"
      @close="clearHover"
      @calendar-change="clearHover"
      @panel-change="clearHover"
    >
      <!-- die Legende erscheint im Kalenderfuß nur, wenn Restriktionen vorhanden sind -->
      <template v-if="hatRestriktionen" #footer>
        <RestriktionenLegende :hinweis="legendeHinweis" :zeige-land-hinweis="landAlsHinweis" />
      </template>
    </AppDatePicker>

    <b-popover
      v-if="hoverDayKey"
      :key="hoverDayKey"
      :target="getHoverZiel"
      :show="true"
      triggers="manual"
      placement="top"
      boundary="viewport"
      custom-class="restriktionen-picker-popover"
    >
      <div v-if="hoverBelegtText">{{ hoverBelegtText }}</div>
      <RestriktionenKalendertagHover v-else :entries="hoverEntries" />
    </b-popover>
  </div>
</template>

<script>
import { reiseterminStateFormatter } from '@/core/common/helpers/utils';
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';
import RestriktionenKalendertagHover from './restriktionen-kalendertag-hover.vue';
import RestriktionenLegende from './restriktionen-legende.vue';
import {
  RESTRIKTION_TYPE,
  RESTRIKTION_TYPE_CONFIG,
  sortRestriktionenByPriority,
} from './restriktionen.constants.js';
import { toDayKey, splitRestriktionenNachQuelle } from './restriktionen-calendar.helper.js';

const TAG_KLASSE_PREFIX = 'restriktion-tag-';

export default {
  name: 'RestriktionenDatePickerComponent',
  components: {
    AppDatePicker,
    RestriktionenKalendertagHover,
    RestriktionenLegende,
  },
  inheritAttrs: false,
  props: {
    value: {
      type: [Date, String],
      default: null,
    },
    restriktionenByDay: {
      type: Object,
      default: () => ({}),
    },
    disabledDate: {
      type: Function,
      default: () => false,
    },
    // Restriktionen des Landes werden dann nur eingefärbt, der Tag bleibt auswählbar
    landAlsHinweis: {
      type: Boolean,
      default: false,
    },
    // Tage mit bereits existierendem Termin sind ausgegraut und nicht wählbar
    belegteTage: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hoverDayKey: null,
    };
  },
  computed: {
    hatRestriktionen() {
      return Object.keys(this.restriktionenByDay).length > 0;
    },
    legendeHinweis() {
      return this.landAlsHinweis
        ? 'Gesperrte Tage sind nicht auswählbar · Restriktionen des Landes sind nur ein Hinweis und bleiben auswählbar · Grund per Hover'
        : 'Gesperrte Tage sind nicht auswählbar · Grund per Hover';
    },
    hoverBelegtText() {
      const state = this.belegteTage[this.hoverDayKey];
      return state ? `Termin existiert (${reiseterminStateFormatter[state] ?? state})` : null;
    },
    hoverEntries() {
      return sortRestriktionenByPriority(this.restriktionenByDay[this.hoverDayKey] || []);
    },
    // die Typfarben werden als CSS-Variablen vererbt, damit das CSS ohne kopierte Farbwerte auskommt
    restriktionenFarbVariablen() {
      return Object.entries(RESTRIKTION_TYPE_CONFIG).reduce((acc, [type, config]) => {
        acc[`--restriktion-${type}-bg`] = config.bgColor;
        acc[`--restriktion-${type}-text`] = config.textColor;
        return acc;
      }, {});
    },
  },
  watch: {
    restriktionenByDay: 'clearHover',
    belegteTage: 'clearHover',
  },
  created() {
    this.$_hoverZelle = null;
  },
  methods: {
    restriktionenFuerDatum(date) {
      return this.restriktionenByDay[toDayKey(date)] || null;
    },
    istDatumDeaktiviert(date, currentValue) {
      if (this.disabledDate(date, currentValue) || toDayKey(date) in this.belegteTage) {
        return true;
      }
      const entries = this.restriktionenFuerDatum(date);
      if (!entries || !entries.length) {
        return false;
      }
      // Restriktionen des Landes sperren den Tag nicht
      return this.landAlsHinweis ? splitRestriktionenNachQuelle(entries).sperren.length > 0 : true;
    },
    // Typ, in dessen Farbe der Tag dargestellt wird, null wenn der Tag frei ist
    anzeigeTypFuerDatum(date) {
      const entries = this.restriktionenFuerDatum(date);
      if (!entries || !entries.length) {
        return null;
      }
      if (!this.landAlsHinweis) {
        return sortRestriktionenByPriority(entries)[0].type;
      }
      const { sperren, landHinweise } = splitRestriktionenNachQuelle(entries);
      if (sperren.length) {
        return sortRestriktionenByPriority(sperren)[0].type;
      }
      return landHinweise.length ? RESTRIKTION_TYPE.LAND_HINWEIS : null;
    },
    // liefert Typklasse und Marker-Klasse für Restriktionstage, freie Tage bleiben unverändert
    getZellenKlassen(cellDate) {
      const dayKey = toDayKey(cellDate);
      if (dayKey in this.belegteTage) {
        return `belegt-cell ${TAG_KLASSE_PREFIX}${dayKey}`;
      }
      const type = this.anzeigeTypFuerDatum(cellDate);
      if (!type) {
        return '';
      }
      return `restriktion-cell restriktion-cell--${type} ${TAG_KLASSE_PREFIX}${dayKey}`;
    },
    getHoverZiel() {
      return this.$_hoverZelle;
    },
    leseDayKey(zelle) {
      const treffer = Array.from(zelle.classList).find(klasse => klasse.startsWith(TAG_KLASSE_PREFIX));
      return treffer ? treffer.slice(TAG_KLASSE_PREFIX.length) : null;
    },
    onKalenderMouseOver(event) {
      const zelle = event.target.closest('td.cell.restriktion-cell, td.cell.belegt-cell');
      const dayKey = zelle ? this.leseDayKey(zelle) : null;
      if (!zelle || !dayKey) {
        this.clearHover();
        return;
      }
      // das native title-Attribut des Datepickers würde sonst zusätzlich zum Popover erscheinen
      zelle.removeAttribute('title');
      if (this.$_hoverZelle === zelle && this.hoverDayKey === dayKey) {
        return;
      }
      this.$_hoverZelle = zelle;
      this.hoverDayKey = dayKey;
    },
    clearHover() {
      this.$_hoverZelle = null;
      if (this.hoverDayKey !== null) {
        this.hoverDayKey = null;
      }
    },
  },
};
</script>

<style scoped>
/*
  Nur die Einfärbung der Restriktionstage liegt hier, das Grundaussehen kommt aus AppDatePicker
*/
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell:hover) {
  font-weight: 600;
}

/* Nur harte Sperren sind nicht anklickbar, Hinweistage des Landes behalten den Zeiger */
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell:not(.restriktion-cell--land-hinweis)),
:deep(
    .app-date-picker .mx-calendar-content td.cell.restriktion-cell:not(.restriktion-cell--land-hinweis):hover
  ) {
  cursor: default;
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise-hart),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise-hart:hover) {
  background-color: var(--restriktion-keine-abreise-hart-bg);
  color: var(--restriktion-keine-abreise-hart-text);
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise:hover) {
  background-color: var(--restriktion-keine-abreise-bg);
  color: var(--restriktion-keine-abreise-text);
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-reisen),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-reisen:hover) {
  background-color: var(--restriktion-keine-reisen-bg);
  color: var(--restriktion-keine-reisen-text);
}

/* Der ausgewählte Tag behält die Auswahlfarbe des Datepickers, deshalb ist active ausgenommen */
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--land-hinweis:not(.active)),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--land-hinweis:not(.active):hover) {
  background-color: var(--restriktion-land-hinweis-bg);
  color: var(--restriktion-land-hinweis-text);
}

:deep(.app-date-picker .mx-calendar-content td.cell.belegt-cell),
:deep(.app-date-picker .mx-calendar-content td.cell.belegt-cell:hover) {
  background-color: var(--surface-sunken);
  color: var(--text-muted);
  cursor: default;
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell.not-current-month) {
  opacity: 0.45;
}
</style>

<style>
.restriktionen-picker-popover.popover {
  z-index: 2100;
  max-width: 380px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-lg);
}

.restriktionen-picker-popover .popover-body {
  padding: 16px;
}
</style>
